import { Icon20Cancel } from '@vkontakte/icons';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { name } from 'reactApp/modules/aiTools/helpers/constants';
import { sendDwhRadar } from 'reactApp/modules/dwh/dwh.helpers';
import styles from 'reactApp/ui/ReactViewer/AiImprove/BackgroundSelector/BackgroundSelector.css';
import type { ItemType } from 'reactApp/ui/ReactViewer/AiImprove/BackgroundSelector/BackgroundSelector.helpers';

const getFilteredItems = (items: ItemType[], activeId = '', isCollapsed = true) => {
    if (isCollapsed) {
        const idx = items.findIndex((item) => item.id === activeId);
        if (idx >= 0) {
            const filtered = [...items];
            return [...filtered.splice(idx, 1), ...filtered];
        }
    }

    return items;
};

const defaultWidth = 120;
const itemWidth = 54;

export const ColorsWidget = memo<{
    items: ItemType[];
    activeBackgroundId: string;
    onCollapseChanged(width: number): void;
}>(function ColorsWidget({ items, activeBackgroundId, onCollapseChanged }) {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [itemsFiltered, setItemsFiltered] = useState(() => getFilteredItems(items, activeBackgroundId));

    const handleClick = useCallback(
        (event) => {
            if (isCollapsed) {
                setIsCollapsed(false);
                event.stopPropagation();
                return;
            }

            setIsCollapsed(true);
        },
        [isCollapsed]
    );

    const handleCollapse = useCallback((event) => {
        setIsCollapsed(true);
        event.stopPropagation();
    }, []);

    useEffect(() => {
        if (isCollapsed) {
            setTimeout(() => setItemsFiltered(getFilteredItems(items, activeBackgroundId, isCollapsed)), 300);
        } else {
            setItemsFiltered(items);
        }
    }, [isCollapsed, activeBackgroundId, items]);

    const width = isCollapsed ? defaultWidth : 44 + itemWidth * Math.ceil(itemsFiltered.length / 2);

    useEffect(() => {
        sendDwhRadar({ t: 'object', action: 'show', name, data: { isCollapsed } });
        onCollapseChanged(width);
    }, [isCollapsed, onCollapseChanged, width]);

    return (
        <div
            className={classNames(styles.colorsWidget, { [styles.collapsed]: isCollapsed })}
            style={{ minWidth: `${width}px`, maxWidth: `${width}px` }}
            onClick={handleClick}
        >
            <div className={styles.colorsWrapper}>
                {itemsFiltered.map((item, idx) => (
                    <div
                        id={item.id}
                        key={item.id}
                        className={classNames(styles.colorItem, {
                            [styles.active]: item.id === activeBackgroundId,
                            [styles.hide]: isCollapsed && idx >= 4,
                        })}
                        style={{
                            background: item.type === 'color' ? item.id : undefined,
                            backgroundImage: item.backgroundImageThumb ? `url(${item.backgroundImageThumb})` : 'none',
                        }}
                    />
                ))}
            </div>
            <div className={styles.closeIcon}>
                <Icon20Cancel onClick={handleCollapse} width={16} height={16} />
            </div>
        </div>
    );
});

ColorsWidget.displayName = 'ColorsWidget';
